import React, { useState, useEffect } from 'react';
import { BaseCard, CustomButton, DailyDiaryDetails, RegistryInternalDetails, AccidentMedicineReportCardInternalDetails } from 'src/app/components';
import { useAppSelector } from 'src/app/services/store';
import { getCurrentChild } from 'src/shared/redux-state/src';
import { fetchTodayDiary } from 'src/shared/services/src';
import { ChildrenStoreData, TimelineData } from 'src/shared/types/src';
import Images from "../../../static";
import { ChevronForward } from 'react-ionicons';
import { ACTIVITY_DETAIL_TYPE, ACTIVITY_TYPE, DATE_FORMATS, MEDICINE_FORM_STATUS } from 'src/shared/config/src';
import { DailyDiaryLoader } from 'src/app/components';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from 'src/app/navigation/routes';
import { navigateToDownloadAppScreen, scrollToTop } from 'src/app/utilities/helpers';
import { dateFormatter } from 'src/shared/utils/src';
import { parse } from "date-fns";
import { ShimmerThumbnail} from "react-shimmer-effects";

const TodayDiaryCard = () => {

    const navigate = useNavigate();

    const currentChild: ChildrenStoreData = useAppSelector(getCurrentChild);

    const [isLoading, setLoading] = useState<boolean>(false);
    const [todayDiaryDetails, setTodayDiaryDetails] = useState<TimelineData[]>([]);

    const [currentRegistryStatus, setCurrentRegistryStatus] = useState<string>('');
    const [currentRegistryInTime, setCurrentRegistryInTime] = useState<string>('');
    const [currentRegistryOutTime, setCurrentRegistryOutTime] = useState<string>('');

    useEffect(() => {
        resetCurrentChildRegistryStatusData();
        getData(currentChild);
    }, [currentChild.childInstanceKey]);

    const getData = async (currentChildLocal: ChildrenStoreData) => {
        try {
            setLoading(true);
            const response = await fetchTodayDiary({ child: currentChildLocal });
            console.log("response from TODAY DIARY api : ", response);
            if (response && Object.keys(response).length > 0) {
                setTodayDiaryDetails(response.timeline);
                setRegistryStateForCurrentChild(response.timeline);
            } else {
                setTodayDiaryDetails([]);
            }
            scrollToTop();
        } catch (error) {
            console.log("error from TODAY DIARY diary api : ", (error as any).response);
            setTodayDiaryDetails([]);
        } finally {
            setLoading(false);
        }
    }

    const setRegistryStateForCurrentChild = (timeline: TimelineData[]) => {
        const latestRegistryEntry = timeline.find((entry) => (entry.activityType === ACTIVITY_TYPE.SIGN_IN || entry.activityType === ACTIVITY_TYPE.SIGN_OUT));
        if (latestRegistryEntry) {
            const activityDate = parse(latestRegistryEntry.timestamp, DATE_FORMATS.YMD_HM, new Date());
            const lastRegistryActivityTime = dateFormatter(activityDate, DATE_FORMATS.TIME_AMPM)
            if (latestRegistryEntry.activityType === ACTIVITY_TYPE.SIGN_IN) {
                setCurrentRegistryStatus('IN');
                setCurrentRegistryInTime(lastRegistryActivityTime);
                setCurrentRegistryOutTime('');
            } else if (latestRegistryEntry.activityType === ACTIVITY_TYPE.SIGN_OUT) {
                setCurrentRegistryStatus('OUT');
                setCurrentRegistryOutTime(lastRegistryActivityTime);
                const lastRegistryInEntry = timeline.find((entry) => (entry.activityType === ACTIVITY_TYPE.SIGN_IN));
                if (lastRegistryInEntry) {
                    const lastRegistryInDate = parse(lastRegistryInEntry.timestamp, DATE_FORMATS.YMD_HM, new Date());
                    const lastRegistryInTime = dateFormatter(lastRegistryInDate, DATE_FORMATS.TIME_AMPM)
                    setCurrentRegistryInTime(lastRegistryInTime);
                }
            } else {
                resetCurrentChildRegistryStatusData();
            }
        }
    }

    const resetCurrentChildRegistryStatusData = () => {
        setCurrentRegistryStatus('');
        setCurrentRegistryInTime('');
        setCurrentRegistryOutTime('');
    }

    const handleReportCardClick = (timelineItem: TimelineData) => {
        if (timelineItem?.activityType === ACTIVITY_TYPE.SUMMATIVE_REPORT) {
            return navigate(AppRoutes.SUMMATIVE_REPORT_DETAILS, { state: { reportID: timelineItem?.id } })
        };

        if (timelineItem?.activityType === ACTIVITY_TYPE.TRANSITION_REPORT) {
            return navigate(AppRoutes.TRANSITION_REPORT_DETAILS, { state: { reportID: timelineItem?.id }})
        };

        if (timelineItem.activityType === ACTIVITY_TYPE.CUSTOM_SUMMATIVE_REPORT) {
            return navigate(AppRoutes.CUSTOM_SUMMATIVE_REPORT, { state: { url: timelineItem?.details?.url, headerTitle: 'Summative Report' } })
        }

        if (timelineItem.activityType === ACTIVITY_TYPE.CUSTOM_TRANSITION_REPORT) {
            return navigate(AppRoutes.CUSTOM_TRANSITION_REPORT, { state: { url: timelineItem?.details?.url, headerTitle: 'Transition Report' } })
        }
    }

    const renderActivityTypeDetails = (item: TimelineData) => {
        switch (item.activityType) {
            case ACTIVITY_TYPE.SIGN_IN:
            case ACTIVITY_TYPE.SIGN_OUT:
                return <div className='daily-diary-item-container'>
                    <RegistryInternalDetails timelineItem={item} />
                </div>
            case ACTIVITY_TYPE.DAILY_DIARY_HOLIDAY:
                return <DailyDiaryDetails details={{ ...item?.details, notes: { ...item?.details?.notesData } }} />
                // return <div className='daily-diary-item-container'>
                //         <RegistryInternalDetails timelineItem={{ ...item?.details, notes: { ...item?.details?.notesData } }} />
                //     </div>
            case ACTIVITY_TYPE.DAILY_DIARY:
                return <>
                    <DailyDiaryDetails details={item.details} />
                </>
            case ACTIVITY_TYPE.ACCIDENT_SHEET:
                return (
                    <div className="daily-diary-item-container cursor-pointer" 
                        onClick={() => navigate(AppRoutes.ACCIDENT_SHEET_DETAILS_SCREEN(item?.id), { state: {headerTitle: item?.content} })}
                    >
                        <AccidentMedicineReportCardInternalDetails
                            type={ACTIVITY_DETAIL_TYPE.ACCIDENT}
                            content={item?.content}
                            status={item?.status}
                            timestamp={item?.timestamp}
                            notes={item?.details?.notes}
                        />
                    </div>
                )
            case ACTIVITY_TYPE.MEDICINE_FORM:
                return (
                    <div className="daily-diary-item-container cursor-pointer"
                        onClick={() => {
                            if (item?.status === MEDICINE_FORM_STATUS.REJECTED) {
                                navigate(AppRoutes.CREATE_UPDATE_MEDICINE_FORM(item?.id))
                            } else {
                                navigate(AppRoutes.MEDICINE_FORM_DETAILS(item?.id))
                            };
                        }}
                    >
                        <AccidentMedicineReportCardInternalDetails
                            type={ACTIVITY_DETAIL_TYPE.MEDICINE}
                            content={item?.content}
                            status={item?.status}
                            timestamp={item?.timestamp}
                            notes={item?.details?.notes}
                        />
                    </div>
                )
            case ACTIVITY_TYPE.CUSTOM_SUMMATIVE_REPORT:
            case ACTIVITY_TYPE.CUSTOM_TRANSITION_REPORT:
            case ACTIVITY_TYPE.SUMMATIVE_REPORT:
            case ACTIVITY_TYPE.TRANSITION_REPORT:
            case ACTIVITY_TYPE.PROGRESS_CHECK_REPORT:
                // return <View style={[commonStyles.eventCardItem, commonStyles.viewPaddingHorizontal, commonStyles.borderSolidTop]}><AccidentMedicineReportCardInternalDetails
                //     type={ACTIVITY_DETAIL_TYPE.REPORT}
                //     content={item?.content}
                //     status={item?.status}
                //     timestamp={item?.timestamp}
                //     notes={item?.details?.notes}
                // /></View>
                return (
                    <div className="daily-diary-item-container cursor-pointer" onClick={() => handleReportCardClick(item)}>
                        <AccidentMedicineReportCardInternalDetails
                            type={ACTIVITY_DETAIL_TYPE.REPORT}
                            content={item?.content}
                            status={item?.status}
                            timestamp={item?.timestamp}
                            notes={item?.details?.notes}
                        />
                    </div>
                );
        }
    }

    const renderSignInButton = () => {
        return (
            <div className="col-6">
                <CustomButton onClick={navigateToDownloadAppScreen} text='Sign in' customBtnStyle='btn-width-img' icon={<img src={Images.scanLight} alt="signIn-icon" />} color='outline-primary' />
            </div>
        )
    }

    const renderReportAbsenseButton = () => {
        return (
            <div className="col-6">
                <CustomButton onClick={navigateToDownloadAppScreen} text='Report Absence ' color='primary' />
            </div>
        )
    }

    const shimmerButtonLoader = () => {
        return (
            <div className="col-6">
                <ShimmerThumbnail height={42} className="rounded-3 mb-0" />
            </div>
        )
    }

    return (
        <BaseCard>
            <div className={`${todayDiaryDetails && todayDiaryDetails.length > 0 ? 'daily-diary-item-container  pd-b-15' : ''}`}>
                <div className="section-with-img">
                    <div className="section-img">
                        <img src={Images.onTimeIconLight} alt="account-summary" />
                    </div>
                    <span className="fs-12 fw-semibold text-black flex-1 pd-x-10">Today</span>
                    {todayDiaryDetails && todayDiaryDetails.length > 0 ? <span className='cursor-pointer' onClick={() => navigate(AppRoutes.TIMELINE)}>
                        <ChevronForward color={'#000000'} height="20px" width="20px" />
                    </span> : null}
                </div>
                {(!currentRegistryStatus) && <div className="row mg-t-20 gutter-15">
                    {isLoading && shimmerButtonLoader()}
                    {!isLoading && renderReportAbsenseButton()}

                    {isLoading && shimmerButtonLoader()}
                    {!isLoading && renderSignInButton()}
                </div>
                }

                {(currentRegistryStatus === 'IN') && <div className="row mg-t-20 gutter-15">
                    {isLoading && shimmerButtonLoader()}
                    {!isLoading && <div className="col-6">
                        <CustomButton onClick={() => {}} text={`Signed In - ${currentRegistryInTime}`} customBtnStyle={'fw-regular cursor-default'}  color='outline-primary' />
                    </div>}

                    {isLoading && shimmerButtonLoader()}
                    {!isLoading && <div className="col-6">
                        <CustomButton onClick={navigateToDownloadAppScreen} text='Sign Out' customBtnStyle='btn-width-img' icon={<img src={Images.scanLight} alt="signIn-icon" />} color='outline-primary' />
                    </div>}
                </div>
                }
                
                {(currentRegistryStatus === 'OUT') && <div className="row mg-t-20 gutter-15">
                    {isLoading && shimmerButtonLoader()}
                    {!isLoading && <div className="col-6">
                        <CustomButton onClick={() => { }} text={`${currentRegistryInTime} - ${currentRegistryOutTime}`} customBtnStyle={'fw-regular cursor-default'} color='outline-primary' />
                    </div>}

                    {isLoading && shimmerButtonLoader()}
                    {!isLoading && renderSignInButton()}
                </div>
                }

            </div>
            <>
                {
                    isLoading ? <div className='mg-t-15 pd-t-15 border-top border-gray-200 mx-n-20 pd-x-20'>
                        <DailyDiaryLoader />
                    </div> :
                        <>
                            {
                                todayDiaryDetails && todayDiaryDetails.length > 0 ?
                                    todayDiaryDetails.map((item) =>
                                        renderActivityTypeDetails(item)
                                    )
                                    : null
                            }
                        </>
                }
            </>
        </BaseCard>
    )
}

export default TodayDiaryCard